/**
 * Offerte-aanvraag — multi-tegel.
 *
 * Eén globale modal (gerenderd in de header via snippets/offerte-request-badge.liquid)
 * gevoed door een offerte-mandje in localStorage. Op elke PDP voegt de
 * "Offerte aanvragen"-knop de huidige tegel toe aan dat mandje en opent de modal.
 * Een header-icoon verschijnt zodra het mandje gevuld is, zodat je de aanvraag
 * vanaf elke pagina kunt heropenen.
 *
 * Server-side (n8n) verwerkt al een `samples[]`-array, dus we sturen simpelweg
 * meerdere tegels mee in hetzelfde contract als voorheen.
 */

const STORAGE_KEY = 'tms_offerte_request';
const TTL_MS = 24 * 60 * 60 * 1000;
const DEFAULT_WASTAGE = 10;
const TILES_URL = '/collections/tegels';
const DEFAULT_SUBMISSION_URL = 'https://tegelmegastore.app.n8n.cloud/webhook/quotation';

const MODAL_SELECTOR = '[data-offerte-request-modal]';
const BADGE_SELECTOR = '[data-offerte-request-badge]';

const DELETE_ICON = `
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">
    <path d="M11.5 9v4.25M8.5 9v4.25M5.75 12.2V6h8.5c0 2.421 0 3.779 0 6.2 0 .853 0 1.447-.038 1.91-.037.453-.106.714-.207.911a2.498 2.498 0 0 1-.983 1.017c-.197.1-.458.17-.911.207-.463.037-1.057.038-1.91.038h-.4c-.853 0-1.447 0-1.91-.038-.453-.037-.714-.106-.911-.207a2.498 2.498 0 0 1-.984-1.017c-.1-.197-.17-.458-.207-.911C5.75 13.647 5.75 13.053 5.75 12.2z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    <path d="M4.25 6h11.5M8 5.25a2 2 0 1 1 4 0" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>
`;

const runtime = {
  rowsSignature: null,
  submitting: false,
  badgeObserver: null,
};

// --- Helpers ----------------------------------------------------------------
function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function nowIso() {
  return new Date().toISOString();
}

function createRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `req-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function clampWastage(value) {
  if (!Number.isFinite(value)) return DEFAULT_WASTAGE;
  return Math.min(100, Math.max(0, value));
}

// Zelfde formule als de PDP m²-calculator.
function computeDozen(m2, snijverlies, m2PerDoos) {
  if (!Number.isFinite(m2) || m2 <= 0) return null;
  if (!Number.isFinite(m2PerDoos) || m2PerDoos <= 0) return null;
  const wastage = Number.isFinite(snijverlies) ? snijverlies : 0;
  return Math.ceil((m2 * (1 + wastage / 100)) / m2PerDoos);
}

function isDozenComputed(item) {
  return Number.isFinite(item.dozen) && item.dozen > 0;
}

function formatDozenText(item) {
  if (isDozenComputed(item)) {
    return `${item.dozen} ${item.dozen === 1 ? 'doos' : 'dozen'} nodig`;
  }
  if (!Number.isFinite(item.m2PerDoos) || item.m2PerDoos <= 0) {
    return 'Aantal dozen wordt handmatig bepaald.';
  }
  return 'Vul je m² in voor het aantal dozen.';
}

// --- State (localStorage) ---------------------------------------------------
function canUseStorage() {
  try {
    const key = '__tms_offerte_test__';
    window.localStorage.setItem(key, '1');
    window.localStorage.removeItem(key);
    return true;
  } catch (_error) {
    return false;
  }
}

function dispatchUpdate(state) {
  document.dispatchEvent(
    new CustomEvent('offerte-request:updated', {
      detail: { state, count: state?.items?.length || 0 },
    })
  );
}

function normalizeState(state) {
  if (!state || typeof state !== 'object') return null;
  return {
    items: Array.isArray(state.items) ? state.items.filter(Boolean) : [],
    meta: state.meta && typeof state.meta === 'object' ? state.meta : { rooms: [], surface: [], comments: '' },
    customer: state.customer && typeof state.customer === 'object' ? state.customer : { name: '', email: '', phone: '', postcode: '', houseNumber: '' },
    requestId: typeof state.requestId === 'string' ? state.requestId : null,
    createdAt: typeof state.createdAt === 'string' ? state.createdAt : nowIso(),
  };
}

function readState() {
  if (!canUseStorage()) return null;
  let parsed = null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    parsed = raw ? JSON.parse(raw) : null;
  } catch (_error) {
    parsed = null;
  }

  const state = normalizeState(parsed);
  if (!state) return null;

  const createdAt = new Date(state.createdAt).getTime();
  if (Number.isNaN(createdAt) || Date.now() - createdAt > TTL_MS) {
    clearState();
    return null;
  }

  return state;
}

function writeState(next) {
  if (!canUseStorage()) return next;

  const normalized = normalizeState(next);
  if (!normalized || normalized.items.length === 0) {
    window.localStorage.removeItem(STORAGE_KEY);
    dispatchUpdate(null);
    return null;
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
  dispatchUpdate(normalized);
  return normalized;
}

function clearState() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (_error) {
    /* noop */
  }
  dispatchUpdate(null);
}

function getState() {
  return (
    readState() || {
      items: [],
      meta: { rooms: [], surface: [], comments: '' },
      customer: { name: '', email: '', phone: '', postcode: '', houseNumber: '' },
      requestId: null,
      createdAt: nowIso(),
    }
  );
}

function getItems() {
  return readState()?.items || [];
}

function getCount() {
  return getItems().length;
}

// Unieke identiteit per regel: een variant heeft voorrang op de product-URL,
// zodat verschillende varianten van hetzelfde product als aparte regels tellen.
function itemKey(item) {
  return (item?.variantId || item?.productUrl || '').toString();
}

function addItem(item) {
  if (!item?.productUrl) return false;
  const key = itemKey(item);
  const state = getState();
  if (state.items.some((existing) => itemKey(existing) === key)) {
    return false;
  }
  writeState({
    ...state,
    items: [...state.items, { ...item, addedAt: nowIso() }],
  });
  return true;
}

function updateItem(key, patch) {
  const state = readState();
  if (!state) return;
  writeState({
    ...state,
    items: state.items.map((item) => (itemKey(item) === key ? { ...item, ...patch } : item)),
  });
}

function removeItem(key) {
  const state = readState();
  if (!state) return;
  writeState({
    ...state,
    items: state.items.filter((item) => itemKey(item) !== key),
  });
}

function setMeta(patch) {
  const state = getState();
  writeState({ ...state, meta: { ...state.meta, ...patch } });
}

function setCustomer(patch) {
  const state = getState();
  writeState({ ...state, customer: { ...state.customer, ...patch } });
}

function ensureRequestId() {
  const state = readState();
  if (!state) return null;
  if (state.requestId) return state.requestId;
  const requestId = createRequestId();
  writeState({ ...state, requestId });
  return requestId;
}

// --- DOM getters ------------------------------------------------------------
function getModal() {
  return document.querySelector(MODAL_SELECTOR);
}

function getBadges() {
  return Array.from(document.querySelectorAll(BADGE_SELECTOR));
}

function getSubmissionUrl() {
  return getModal()?.dataset.submissionUrl?.trim() || DEFAULT_SUBMISSION_URL;
}

function getTilesUrl() {
  return getModal()?.dataset.tilesUrl?.trim() || TILES_URL;
}

// --- Rendering --------------------------------------------------------------
function updateBadge() {
  const count = getCount();
  getBadges().forEach((badge) => {
    badge.hidden = count === 0;
    badge.setAttribute('aria-hidden', count === 0 ? 'true' : 'false');
    badge.querySelectorAll('[data-offerte-request-count]').forEach((el) => {
      el.textContent = String(count);
    });
  });
}

function hasBadgeInNode(node) {
  if (!(node instanceof Element)) return false;
  return node.matches(BADGE_SELECTOR) || Boolean(node.querySelector(BADGE_SELECTOR));
}

// Klopt de DOM-staat van de badge nog met het mandje, of is hij door een
// header-morph teruggezet naar de server-markup (hidden)?
function isBadgeOutOfSync() {
  const expected = String(getCount());
  const expectHidden = expected === '0';
  return getBadges().some((badge) => {
    if (badge.hidden !== expectHidden) return true;
    const countEl = badge.querySelector('[data-offerte-request-count]');
    return Boolean(countEl) && countEl.textContent !== expected;
  });
}

// Het thema rendert de header client-side opnieuw (o.a. na page load en bij
// cart-updates), waardoor de badge terugvalt op de Liquid-markup (hidden).
// We her-synchroniseren zodra de badge opnieuw in de DOM verschijnt.
function observeBadgeMounts() {
  if (runtime.badgeObserver || typeof MutationObserver === 'undefined' || !document.body) return;

  runtime.badgeObserver = new MutationObserver((mutations) => {
    let needsResync = false;

    for (const mutation of mutations) {
      if (
        mutation.type === 'childList' &&
        Array.from(mutation.addedNodes).some((node) => hasBadgeInNode(node))
      ) {
        needsResync = true;
        break;
      }
      const target = mutation.target;
      const targetEl = target instanceof Element ? target : target?.parentElement || null;
      if (targetEl?.closest?.(BADGE_SELECTOR)) {
        needsResync = true;
        break;
      }
    }

    if (!needsResync || !isBadgeOutOfSync()) return;
    updateBadge();
    bindModalClose();
  });

  runtime.badgeObserver.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true,
  });
}

function renderTileList(modal) {
  const list = modal.querySelector('[data-offerte-request-tile-list]');
  const countEl = modal.querySelector('[data-offerte-request-tile-count]');
  if (!list) return;

  const items = getItems();
  if (countEl) countEl.textContent = String(items.length);

  const tilesUrl = getTilesUrl();
  const addMore = `
    <li>
      <a href="${tilesUrl}" class="offerte-request-add-more" data-offerte-request-add-more>
        <span class="offerte-request-add-more__icon" aria-hidden="true">+</span>
        <span class="offerte-request-add-more__title">Meer tegels toevoegen</span>
      </a>
    </li>
  `;

  if (!items.length) {
    list.innerHTML = `<li class="offerte-request-empty">Nog geen tegels toegevoegd.</li>${addMore}`;
    return;
  }

  list.innerHTML = `${items
    .map((item) => {
      const image = item.imageUrl
        ? `<img src="${escapeHtml(item.imageUrl)}" alt="${escapeHtml(item.productTitle)}" loading="lazy" width="40" height="40">`
        : '<span class="sample-request__sample-thumb-placeholder"></span>';

      const typeMarkup = item.productType
        ? `<p class="sample-request__sample-type">${escapeHtml(item.productType)}</p>`
        : '';

      return `
        <li class="sample-request__sample-item">
          <div class="sample-request__sample-thumb">${image}</div>
          <div class="sample-request__sample-content">
            <div class="sample-request__sample-link">
              ${typeMarkup}
              <p class="sample-request__sample-title">${escapeHtml(item.productTitle)}</p>
            </div>
          </div>
          <button type="button" class="sample-request__sample-remove" data-offerte-remove="${encodeURIComponent(itemKey(item))}" aria-label="Verwijder ${escapeHtml(item.productTitle)}">
            <span class="sample-request__sample-remove-label">Verwijderen</span>
            <span class="sample-request__sample-remove-icon">${DELETE_ICON}</span>
          </button>
        </li>
      `;
    })
    .join('')}${addMore}`;
}

// Bouwt de m²-rijen alleen opnieuw als de set tegels wijzigt, zodat focus in de
// invoervelden bewaard blijft tijdens het typen.
function renderRows(modal, { force = false } = {}) {
  const rowsEl = modal.querySelector('[data-offerte-request-rows]');
  if (!rowsEl) return;

  const items = getItems();
  const signature = items.map((item) => itemKey(item)).join('|');
  if (!force && signature === runtime.rowsSignature) return;
  runtime.rowsSignature = signature;

  if (!items.length) {
    rowsEl.innerHTML = '<li class="offerte-request-empty">Voeg een tegel toe om de m² te berekenen.</li>';
    return;
  }

  rowsEl.innerHTML = items
    .map((item) => {
      const snijverlies = Number.isFinite(item.snijverlies) ? item.snijverlies : DEFAULT_WASTAGE;
      const m2Value = Number.isFinite(item.m2) ? item.m2 : '';
      const key = encodeURIComponent(itemKey(item));
      const mutedClass = isDozenComputed(item) ? '' : ' sample-request-offerte__row-result--muted';

      return `
        <li class="sample-request-offerte__row" data-offerte-row="${key}">
          <p class="sample-request-offerte__row-title">${escapeHtml(item.productTitle)}</p>
          <div class="sample-request-offerte__row-fields">
            <label class="sample-request-offerte__field">
              <span>Aantal m²</span>
              <span class="sample-request-offerte__input-wrap">
                <input type="number" min="0" step="0.01" inputmode="decimal" value="${m2Value}" placeholder="0" data-offerte-m2>
                <span class="sample-request-offerte__input-suffix">m²</span>
              </span>
            </label>
            <label class="sample-request-offerte__field">
              <span>Snijverlies</span>
              <span class="sample-request-offerte__input-wrap">
                <input type="number" min="0" max="100" step="0.1" inputmode="decimal" value="${snijverlies}" data-offerte-snijverlies>
                <span class="sample-request-offerte__input-suffix">%</span>
              </span>
            </label>
          </div>
          <p class="sample-request-offerte__row-result${mutedClass}" data-offerte-result>${formatDozenText(item)}</p>
        </li>
      `;
    })
    .join('');
}

function restoreFormFields(modal) {
  const state = getState();

  modal.querySelectorAll('[data-offerte-request-rooms] [data-offerte-option]').forEach((chip) => {
    chip.setAttribute('aria-pressed', state.meta.rooms?.includes(chip.dataset.value) ? 'true' : 'false');
  });
  modal.querySelectorAll('[data-offerte-request-surface] [data-offerte-option]').forEach((chip) => {
    chip.setAttribute('aria-pressed', state.meta.surface?.includes(chip.dataset.value) ? 'true' : 'false');
  });

  const comments = modal.querySelector('[data-offerte-comments]');
  if (comments) comments.value = state.meta.comments || '';

  const name = modal.querySelector('[data-offerte-name]');
  const email = modal.querySelector('[data-offerte-email]');
  const phone = modal.querySelector('[data-offerte-phone]');
  const postcode = modal.querySelector('[data-offerte-postcode]');
  const houseNumber = modal.querySelector('[data-offerte-housenumber]');
  if (name) name.value = state.customer.name || '';
  if (email) email.value = state.customer.email || '';
  if (phone) phone.value = state.customer.phone || '';
  if (postcode) postcode.value = state.customer.postcode || '';
  if (houseNumber) houseNumber.value = state.customer.houseNumber || '';
}

function renderAll({ force = false } = {}) {
  updateBadge();
  const modal = getModal();
  if (!modal) return;
  renderTileList(modal);
  renderRows(modal, { force });
}

// --- Modal open/close -------------------------------------------------------
function openModal() {
  const modal = getModal();
  if (!modal) return;
  if (!getCount()) return;

  const body = modal.querySelector('[data-offerte-request-body]');
  const success = modal.querySelector('[data-offerte-request-success]');
  const error = modal.querySelector('[data-offerte-request-error]');
  if (body) body.hidden = false;
  if (success) success.hidden = true;
  if (error) {
    error.hidden = true;
    error.textContent = '';
  }

  renderAll({ force: true });
  restoreFormFields(modal);

  if (typeof modal.showModal === 'function') {
    if (!modal.open) modal.showModal();
  } else {
    modal.setAttribute('open', '');
  }
  document.body.style.overflow = 'hidden';
}

function closeModal() {
  const modal = getModal();
  document.body.style.overflow = '';
  if (!modal) return;
  if (typeof modal.close === 'function') {
    if (modal.open) modal.close();
  } else {
    modal.removeAttribute('open');
  }
}

// --- Input handlers ---------------------------------------------------------
function handleRowInput(row) {
  const key = decodeURIComponent(row.dataset.offerteRow || '');
  if (!key) return;

  const item = getItems().find((entry) => itemKey(entry) === key);
  if (!item) return;

  const m2Input = row.querySelector('[data-offerte-m2]');
  const snijverliesInput = row.querySelector('[data-offerte-snijverlies]');
  const resultEl = row.querySelector('[data-offerte-result]');

  m2Input?.classList.remove('offerte-request-input--error');

  const m2Parsed = m2Input && m2Input.value !== '' ? Number.parseFloat(m2Input.value) : null;
  const m2 = Number.isFinite(m2Parsed) ? m2Parsed : null;

  const svParsed =
    snijverliesInput && snijverliesInput.value !== '' ? Number.parseFloat(snijverliesInput.value) : DEFAULT_WASTAGE;
  const snijverlies = clampWastage(Number.isFinite(svParsed) ? svParsed : DEFAULT_WASTAGE);
  if (snijverliesInput && Number.parseFloat(snijverliesInput.value) > 100) {
    snijverliesInput.value = '100';
  }

  const dozen = computeDozen(m2, snijverlies, item.m2PerDoos);
  updateItem(key, { m2, snijverlies, dozen });

  if (resultEl) {
    const updated = { ...item, m2, snijverlies, dozen };
    resultEl.textContent = formatDozenText(updated);
    resultEl.classList.toggle('sample-request-offerte__row-result--muted', !isDozenComputed(updated));
  }
}

function collectChips(modal, groupSelector) {
  return Array.from(modal.querySelectorAll(`${groupSelector} [data-offerte-option][aria-pressed="true"]`)).map(
    (chip) => chip.dataset.value
  );
}

function persistChips(modal) {
  setMeta({
    rooms: collectChips(modal, '[data-offerte-request-rooms]'),
    surface: collectChips(modal, '[data-offerte-request-surface]'),
  });
}

function readSourceProduct(root) {
  const m2PerDoosRaw = root.dataset.productM2PerDoos ? Number.parseFloat(root.dataset.productM2PerDoos) : null;
  return {
    productTitle: root.dataset.productTitle || '',
    productType: root.dataset.productType || '',
    sku: root.dataset.productSku || '',
    variantId: root.dataset.productVariantId || '',
    m2PerDoos: Number.isFinite(m2PerDoosRaw) ? m2PerDoosRaw : null,
    imageUrl: root.dataset.productImage || '',
    productUrl: root.dataset.productUrl || '',
    snijverlies: root.dataset.defaultWastage ? clampWastage(Number.parseFloat(root.dataset.defaultWastage)) : DEFAULT_WASTAGE,
    m2: null,
    dozen: null,
  };
}

// --- Submit -----------------------------------------------------------------
function showError(modal, message) {
  const error = modal.querySelector('[data-offerte-request-error]');
  if (!error) return;
  error.hidden = false;
  error.textContent = message;
}

function clearError(modal) {
  const error = modal.querySelector('[data-offerte-request-error]');
  if (!error) return;
  error.hidden = true;
  error.textContent = '';
}

async function submit() {
  if (runtime.submitting) return;

  const modal = getModal();
  if (!modal) return;

  const items = getItems();
  if (!items.length) {
    showError(modal, 'Voeg eerst een tegel toe.');
    return;
  }

  clearError(modal);

  // Validatie per tegel: m² verplicht.
  let firstInvalid = null;
  modal.querySelectorAll('[data-offerte-row]').forEach((row) => {
    // Flush de actuele DOM-waarden naar state, zodat de payload nooit een
    // geldig ogend veld mist (bv. bij autofill zonder input-event).
    handleRowInput(row);
    const m2Input = row.querySelector('[data-offerte-m2]');
    const m2 = m2Input ? Number.parseFloat(m2Input.value) : NaN;
    const valid = m2Input?.value !== '' && Number.isFinite(m2) && m2 > 0;
    if (m2Input) m2Input.classList.toggle('offerte-request-input--error', !valid);
    if (!valid && !firstInvalid) firstInvalid = m2Input;
  });

  const nameInput = modal.querySelector('[data-offerte-name]');
  const emailInput = modal.querySelector('[data-offerte-email]');
  const phoneInput = modal.querySelector('[data-offerte-phone]');
  const postcodeInput = modal.querySelector('[data-offerte-postcode]');
  const houseNumberInput = modal.querySelector('[data-offerte-housenumber]');
  const name = nameInput?.value.trim() || '';
  const email = emailInput?.value.trim() || '';
  const phone = phoneInput?.value.trim() || '';
  const postcode = postcodeInput?.value.trim() || '';
  const houseNumber = houseNumberInput?.value.trim() || '';

  const invalidate = (field) => {
    field?.classList.add('offerte-request-input--error');
    if (!firstInvalid) firstInvalid = field;
  };
  if (!name) invalidate(nameInput);
  if (!phone) invalidate(phoneInput);
  if (!email || !isValidEmail(email)) invalidate(emailInput);
  if (!postcode) invalidate(postcodeInput);
  if (!houseNumber) invalidate(houseNumberInput);

  if (firstInvalid) {
    showError(modal, 'Vul voor elke tegel de m² in, plus je naam, telefoon, e-mailadres, postcode en huisnummer.');
    firstInvalid.focus?.();
    return;
  }

  setCustomer({ name, email, phone, postcode, houseNumber });
  persistChips(modal);
  const comments = modal.querySelector('[data-offerte-comments]')?.value.trim() || '';
  setMeta({ comments });

  const requestId = ensureRequestId();
  const state = getState();

  const contract = {
    requestId,
    source: 'offerteformulier',
    tags: ['offerte-aanvraag', 'via-offerteformulier'],
    customer: { name, email, phone, postcode, houseNumber },
    location: '',
    rooms: state.meta.rooms || [],
    surface: state.meta.surface || [],
    comments,
    samples: state.items.map((item) => ({
      variantId: item.variantId || '',
      title: item.productTitle || '',
      sku: item.sku || '',
      imageUrl: item.imageUrl || '',
      m2: Number.isFinite(item.m2) ? item.m2 : null,
      snijverlies: Number.isFinite(item.snijverlies) ? item.snijverlies : null,
      m2PerDoos: Number.isFinite(item.m2PerDoos) ? item.m2PerDoos : null,
      dozen: Number.isFinite(item.dozen) ? item.dozen : null,
    })),
  };

  const submitBtn = modal.querySelector('[data-offerte-request-submit]');
  runtime.submitting = true;
  if (submitBtn) submitBtn.disabled = true;

  try {
    // text/plain = "simple request": browser slaat de CORS-preflight over.
    const response = await fetch(getSubmissionUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body: JSON.stringify(contract),
    });

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`);
    }

    const body = modal.querySelector('[data-offerte-request-body]');
    const success = modal.querySelector('[data-offerte-request-success]');
    if (body) body.hidden = true;
    if (success) success.hidden = false;

    // Offerte-velden voor de dataLayer (geen PII): totaal m², het gekozen
    // snijverlies (eerste ingevulde regel), de ruimtes en de plaatsing.
    const offerteM2 =
      Math.round(
        contract.samples.reduce((sum, sample) => sum + (Number.isFinite(sample.m2) ? sample.m2 : 0), 0) * 100
      ) / 100;
    const firstSnijverlies = contract.samples.find((sample) => Number.isFinite(sample.snijverlies));

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'quotation_request_submitted',
      source: 'offerteformulier',
      request_id: requestId,
      tiles_count: contract.samples.length,
      offerte_m2: offerteM2,
      offerte_snijverlies_pct: firstSnijverlies ? firstSnijverlies.snijverlies : null,
      offerte_rooms: (contract.rooms || []).join(', '),
      offerte_placement: (contract.surface || []).join(', '),
    });

    runtime.rowsSignature = null;
    clearState();
  } catch (_error) {
    showError(modal, 'Er ging iets mis bij het versturen. Probeer het zo opnieuw.');
  } finally {
    runtime.submitting = false;
    if (submitBtn) submitBtn.disabled = false;
  }
}

// --- Global event delegation ------------------------------------------------
function bindEvents() {
  if (document.documentElement.dataset.offerteRequestBound === 'true') return;
  document.documentElement.dataset.offerteRequestBound = 'true';

  document.addEventListener('click', (event) => {
    const addTrigger = event.target.closest('[data-offerte-request-add]');
    if (addTrigger) {
      event.preventDefault();
      const root = addTrigger.closest('[data-offerte-request-source]') || addTrigger;
      const product = readSourceProduct(root);
      if (product.productUrl) {
        // addItem dedupliceert zelf op variant + product-URL.
        addItem(product);
      }
      openModal();
      return;
    }

    if (event.target.closest('[data-offerte-request-badge]')) {
      openModal();
      return;
    }

    const modal = getModal();
    if (!modal) return;

    if (event.target.closest('[data-offerte-request-close]')) {
      closeModal();
      return;
    }

    // Backdrop-klik op de dialog sluit de modal.
    if (event.target === modal) {
      closeModal();
      return;
    }

    const removeBtn = event.target.closest('[data-offerte-remove]');
    if (removeBtn && modal.contains(removeBtn)) {
      event.preventDefault();
      const key = decodeURIComponent(removeBtn.getAttribute('data-offerte-remove') || '');
      removeItem(key);
      if (!getCount()) {
        closeModal();
      } else {
        renderAll({ force: true });
      }
      return;
    }

    const addMore = event.target.closest('[data-offerte-request-add-more]');
    if (addMore && modal.contains(addMore)) {
      // Bewaar de huidige invoer voordat we wegnavigeren.
      persistChips(modal);
      const comments = modal.querySelector('[data-offerte-comments]')?.value.trim() || '';
      const name = modal.querySelector('[data-offerte-name]')?.value.trim() || '';
      const email = modal.querySelector('[data-offerte-email]')?.value.trim() || '';
      const phone = modal.querySelector('[data-offerte-phone]')?.value.trim() || '';
      const postcode = modal.querySelector('[data-offerte-postcode]')?.value.trim() || '';
      const houseNumber = modal.querySelector('[data-offerte-housenumber]')?.value.trim() || '';
      setMeta({ comments });
      setCustomer({ name, email, phone, postcode, houseNumber });
      return; // laat de <a> navigeren
    }

    const chip = event.target.closest('[data-offerte-option]');
    if (chip && modal.contains(chip)) {
      const pressed = chip.getAttribute('aria-pressed') === 'true';
      chip.setAttribute('aria-pressed', String(!pressed));
      persistChips(modal);
      return;
    }

    if (event.target.closest('[data-offerte-request-submit]')) {
      submit();
    }
  });

  document.addEventListener('input', (event) => {
    const modal = getModal();
    if (!modal || !modal.contains(event.target)) return;

    const row = event.target.closest('[data-offerte-row]');
    if (row) {
      handleRowInput(row);
      return;
    }

    if (event.target.matches('[data-offerte-comments]')) {
      setMeta({ comments: event.target.value });
      return;
    }

    if (event.target.matches('[data-offerte-name]')) {
      event.target.classList.remove('offerte-request-input--error');
      setCustomer({ name: event.target.value });
      return;
    }
    if (event.target.matches('[data-offerte-email]')) {
      event.target.classList.remove('offerte-request-input--error');
      setCustomer({ email: event.target.value });
      return;
    }
    if (event.target.matches('[data-offerte-phone]')) {
      event.target.classList.remove('offerte-request-input--error');
      setCustomer({ phone: event.target.value });
      return;
    }
    if (event.target.matches('[data-offerte-postcode]')) {
      event.target.classList.remove('offerte-request-input--error');
      setCustomer({ postcode: event.target.value });
      return;
    }
    if (event.target.matches('[data-offerte-housenumber]')) {
      event.target.classList.remove('offerte-request-input--error');
      setCustomer({ houseNumber: event.target.value });
    }
  });

  // Header kan opnieuw renderen (cart morph) → badge opnieuw syncen.
  document.addEventListener('cart:update', () => requestAnimationFrame(updateBadge));
  document.addEventListener('cart:error', () => requestAnimationFrame(updateBadge));
  document.addEventListener('offerte-request:updated', () => renderAll());
}

function bindModalClose() {
  const modal = getModal();
  if (!modal || modal.dataset.offerteCloseBound === 'true') return;
  modal.dataset.offerteCloseBound = 'true';
  // Native <dialog> sluit ook via Escape; herstel dan de body-scroll.
  modal.addEventListener('close', () => {
    document.body.style.overflow = '';
  });
}

function init() {
  bindEvents();
  bindModalClose();
  updateBadge();
  observeBadgeMounts();
  // Vangnet voor thema's die de header iets later hydrateren.
  window.setTimeout(() => {
    bindModalClose();
    updateBadge();
  }, 300);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
